<i18n src="./resources/locales.json" />

<template>
  <v-dialog v-model="dialog" persistent max-width="720" content-class="working-hrs-dialog">
    <v-card color="surface lighten-1">
      <v-card-title class="header">{{ title }}</v-card-title>

      <v-stepper v-model="step" non-linear>
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1" editable edit-icon="mdi-check">
            {{ $t('steps.basicSetup') }}
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="step > 2" step="2" editable edit-icon="mdi-check">
            {{ $t('steps.workingHrs') }}
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="3" editable edit-icon="mdi-check">
            {{ $t('steps.holidays') }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <div class="stepper-page">
              <div class="two-column-form">
                <v-text-field v-model="configuration.description" class="form-field" :label="$t('configName')" />
                <v-select
                  v-model="configuration.businessProcessId"
                  class="form-field"
                  :items="businessProcesses"
                  item-value="id"
                  item-text="name"
                  :label="$t('businessProcesses')"
                  @change="changeBusinessProcess"
                />

                <v-text-field v-model="configuration.email" class="form-field" :label="$t('email')" />
                <v-text-field v-model="configuration.phone" class="form-field" :label="$t('phone')" />

                <v-select
                  v-model="configuration.openChatOfferAfter"
                  class="form-field"
                  :items="delayedStartOptions"
                  item-value="value"
                  item-text="label"
                  :label="$t('popUpChatAfter')"
                  @change="changeDelayedStart"
                />
              </div>

              <div v-if="configuration.defaultCustomer" class="form-group pt-0">
                <div class="form-elements">
                  <v-checkbox
                    v-model="configuration.defaultCustomer.enabled"
                    :label="$t('defaultCustomer.useDefaultCustomer')"
                  />
                  <div v-show="configuration.defaultCustomer.enabled" class="two-column-form">
                    <v-text-field
                      v-model="configuration.defaultCustomer.name"
                      class="form-field"
                      :label="$t('defaultCustomer.name')"
                    />
                    <v-text-field
                      v-model="configuration.defaultCustomer.email"
                      class="form-field"
                      :label="$t('defaultCustomer.email')"
                    />
                  </div>
                  <div
                    v-show="configuration.defaultCustomer.enabled && configuration.defaultCustomer.email"
                    class="note"
                  >
                    {{ $t('defaultCustomer.note', { email: customerEmailExample }) }}
                  </div>
                </div>
              </div>
              <div class="form-group two-column-form">
                <v-text-field v-model="configuration.welcomeMessage" :label="$t('welcomeMessage')" />
                <v-text-field v-model="configuration.offerMessage" :label="$t('offerMessage')" />
              </div>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2">
            <div class="stepper-page">
              <div class="time-zone">
                <div></div>
                <div>
                  <v-select
                    v-model="configuration.ianaTimeZone"
                    class="form-field large"
                    :items="timeZones"
                    item-value="ianaTimeZone"
                    item-text="displayName"
                    :label="$t('schedule.timeZone')"
                  />
                </div>
              </div>

              <div
                v-for="([label, workingHrs], i) in Object.entries(configuration.workingHrsSchedule)"
                :key="i"
                class="day-row"
              >
                <div class="day-label">{{ $t(`schedule.${label}`) }}</div>

                <div v-for="(workingHr, j) in workingHrs" :key="j" class="from-to">
                  <v-select
                    v-model="workingHr.from"
                    :items="options"
                    item-value="value"
                    item-text="label"
                    :label="$t('schedule.from')"
                    class="delayed-start"
                  />
                  <v-select
                    v-model="workingHr.to"
                    :items="options"
                    item-value="value"
                    item-text="label"
                    :label="$t('schedule.to')"
                    class="delayed-start"
                  />
                </div>

                <div class="actions">
                  <v-btn icon @click="() => fillBusinessHrs(label)"><v-icon>mdi-briefcase-clock</v-icon></v-btn>
                  <v-btn icon @click="() => clearRow(label)"><v-icon>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
          </v-stepper-content>

          <v-stepper-content step="3">
            <div class="stepper-page">
              <div v-for="(holiday, i) in fixedHolidays" :key="i" class="day-row">
                <div class="day-label">
                  {{ holiday.name }}
                  <div class="detail">
                    {{ formatDate(holiday.date) }}
                  </div>
                </div>

                <div v-for="(index, j) in [0, 1]" :key="j" class="from-to">
                  <v-select
                    :value="getHoliday(holiday.date, index, 'from')"
                    :items="options"
                    item-value="value"
                    item-text="label"
                    :label="$t('schedule.from')"
                    class="delayed-start"
                    @change="value => setHoliday(holiday, index, 'from', value)"
                  />
                  <v-select
                    :value="getHoliday(holiday.date, index, 'to')"
                    :items="options"
                    item-value="value"
                    item-text="label"
                    :label="$t('schedule.to')"
                    class="delayed-start"
                    @change="value => setHoliday(holiday, index, 'to', value)"
                  />
                </div>

                <div class="actions">
                  <v-btn icon @click="() => clearHolidayRow(holiday)"><v-icon>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card-actions>
        <v-btn v-if="step > 1" text @click="step = --step">
          {{ $t('buttons.previous') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-btn v-if="step < 3 && configuration._id" text @click="accept">
          {{ $t('buttons.update') }}
        </v-btn>
        <v-btn v-if="step < 3" class="primary" @click="step = ++step">
          {{ $t('buttons.next') }}
        </v-btn>
        <v-btn v-else class="primary" @click="accept">
          {{ configuration._id ? $t('buttons.update') : $t('buttons.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import ConfigurationService from '@/services/configuration.service';
import moment from 'moment';
import { v4 as UUID } from 'uuid';
import DateTime from 'luxon/src/datetime';
import { WindowsToIanaTimeZones } from './windowsTimeZones';

const EMPTY_CONFIGURATION = {
  orgId: '',
  businessProcessId: '',
  businessProcessEndPoint: '',
  description: 'New Configuration',
  openChatOfferAfter: 30,
  welcomeMessage: 'Welcome to EvolveIP!',
  offerMessage: 'Hey! Would you like to get some help?',
  email: '',
  phone: '',
  defaultCustomer: {
    enabled: false,
    name: '',
    email: ''
  },
  ianaTimeZone: '',
  msTimeZone: '',
  workingHrsSchedule: {
    monday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    tuesday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    wednesday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    thursday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    friday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    saturday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ],
    sunday: [
      { from: undefined, to: undefined },
      { from: undefined, to: undefined }
    ]
  },
  holidays: [],
  enabled: true
};

export default {
  name: 'EcsConfigurationWizardDialog',

  data() {
    return {
      title: '',
      dialog: false,
      callback: null,
      step: 1,
      options: [],
      holidays: {},
      orgHolidays: [],
      timeZones: [],
      fixedHolidays: [
        {
          date: `${new Date().getFullYear()}-01-01T00:00:00.000Z`,
          name: `New Year's Day`
        },
        {
          date: `${new Date().getFullYear()}-07-04T00:00:00.000Z`,
          name: `Independence Day`
        },
        {
          date: `${new Date().getFullYear()}-09-05T00:00:00.000Z`,
          name: `Labor Day`
        },
        {
          date: `${new Date().getFullYear()}-11-24T00:00:00.000Z`,
          name: `Thanksgiving`
        },
        {
          date: `${new Date().getFullYear()}-12-25T00:00:00.000Z`,
          name: `Christmas Day`
        }
      ],
      delayedStartOptions: [
        {
          label: 'Never',
          value: -1
        },
        {
          label: 'Instantly',
          value: 0
        },
        {
          label: '15 Seconds',
          value: 15
        },
        {
          label: '30 Seconds',
          value: 30
        },
        {
          label: '1 Minute',
          value: 60
        },
        {
          label: '3 Minutes',
          value: 60 * 3
        },
        {
          label: '5 Minutes',
          value: 60 * 5
        }
      ],
      configuration: EMPTY_CONFIGURATION
    };
  },

  computed: {
    ...mapGetters(['businessProcesses', 'organizationId']),

    customerEmailExample() {
      return `${UUID().split('-')[0]}.${this.configuration.defaultCustomer.email}`;
    }
  },

  created() {
    this.options.push({ label: '' });

    for (let i = 0; i < 24; ++i) {
      this.options.push({ label: `${i}:00`, value: i * 60 });
      this.options.push({ label: `${i}:15`, value: i * 60 + 15 });
      this.options.push({ label: `${i}:30`, value: i * 60 + 30 });
      this.options.push({ label: `${i}:45`, value: i * 60 + 45 });
    }

    ConfigurationService.getConfiguration().then(data => {
      // Holidays
      const specialDays = data.configuration.organizationData.specialDays || [];
      const calendars = data.configuration.organizationData.calendars || {};
      const calendar2021 = calendars.find(calendar => calendar.year >= 2020);
      this.orgHolidays = specialDays.map(day => {
        const calendarDay = calendar2021.days.find(d => d.specialDay === day.id);
        return {
          ...day,
          date: (calendarDay || {}).date
        };
      });

      // TimeZone Example.
      // {
      //   'value': 78,
      //   'name': 'Eastern Standard Time',
      //   'displayName': '(UTC-05:00) Eastern Time (US & Canada)',
      //   'baseUtcOffset': '-05:00:00',
      //   'daylightName': 'Eastern Daylight Time',
      //   'id': 'Eastern Standard Time',
      //   'standardName': 'Eastern Standard Time',
      //   'supportsDaylightSavingTime': true
      // }
      const timeZones = data.configuration.organizationData.preferredTimezones || [];
      timeZones.forEach(item => {
        item.ianaTimeZone = WindowsToIanaTimeZones[item.id];
        const dt = DateTime.local().setZone(item.ianaTimeZone);
        item.displayName = dt.isInDST ? item.daylightName : item.standardName;
      });
      this.timeZones = timeZones;

      // Default customer fallback
      if (!data.configuration.defaultCustomer) {
        data.configuration.defaultCustomer = { enabled: false };
      }
    });
  },

  watch: {
    timeZones() {
      if (!this.configuration.ianaTimeZone) {
        this.configuration.ianaTimeZone = this.timeZones[0].ianaTimeZone;
      }
    },
    'configuration.ianaTimeZone'(newValue) {
      this.configuration.msTimeZone = this.timeZones.find(tz => tz.ianaTimeZone === newValue).displayName;
    }
  },

  mounted() {
    this.$root.$on('open-configuration-dialog', this.open);
  },

  beforeDestroy() {
    this.$root.$off('open-configuration-dialog');
  },

  methods: {
    open({ title = null, configuration = null, callback = null } = {}) {
      this.dialog = true;
      this.title = title || this.$t('header.title');
      this.callback = callback;
      this.configuration = structuredClone(configuration) || structuredClone(EMPTY_CONFIGURATION);
      this.configuration.orgId = this.organizationId;

      if (!this.configuration.businessProcessId) {
        this.configuration.businessProcessId = this.businessProcesses[0].id;
        this.configuration.businessProcessEndPoint = this.businessProcesses[0].endPoint;
      }

      if (!this.configuration.defaultCustomer) {
        this.configuration.defaultCustomer = { enabled: false };
      }

      this.step = 1;
      return new Promise(resolve => {
        this.$once('confirm', resolve);
      });
    },

    getTimeOffsetFromMsId(microsoftTimeZoneId) {
      return DateTime.local().setZone(WindowsToIanaTimeZones[microsoftTimeZoneId]).offset;
    },

    fillBusinessHrs(label) {
      this.configuration.workingHrsSchedule[label][0].from = 9 * 60;
      this.configuration.workingHrsSchedule[label][0].to = 17 * 60;
    },

    clearRow(label) {
      this.configuration.workingHrsSchedule[label][0].from = '';
      this.configuration.workingHrsSchedule[label][0].to = '';
      this.configuration.workingHrsSchedule[label][1].from = '';
      this.configuration.workingHrsSchedule[label][1].to = '';
    },

    clearHolidayRow({ date }) {
      const holidayIndex = this.configuration.holidays.findIndex(holiday => holiday.date === date);

      if (holidayIndex !== -1) {
        this.configuration.holidays.splice(holidayIndex, 1);
      }
    },

    changeDelayedStart() {},

    changeBusinessProcess(id) {
      const businessProcess = this.businessProcesses.find(bp => bp.id === id);
      if (businessProcess) {
        this.configuration.businessProcessEndPoint = businessProcess.endPoint;
      }
    },

    getHoliday(date, index, key) {
      const holiday = this.configuration.holidays.find(holiday => holiday.date === date);
      if (holiday && holiday.schedule && holiday.schedule[index]) {
        return holiday.schedule[index][key];
      } else {
        return '';
      }
    },

    setHoliday(date, index, key, value) {
      if (!this.configuration.holidays) {
        this.configuration.holidays = [];
      }

      let holiday = this.configuration.holidays.find(holiday => holiday.date === date.date);

      if (!holiday) {
        this.configuration.holidays.push({
          ...date,
          schedule: [
            { from: undefined, to: undefined },
            { from: undefined, to: undefined }
          ]
        });
      }

      holiday = this.configuration.holidays.find(holiday => holiday.date === date.date);
      holiday.schedule[index][key] = value;
    },

    formatDate(date) {
      return moment(date).format('MMMM Do');
    },

    accept() {
      this.dialog = false;
      this.callback && this.callback(this.configuration);
      this.$emit('confirm', this.configuration);
    },

    cancel() {
      this.dialog = false;
      this.$emit('confirm', false);
    }
  }
};
</script>

<style scoped lang="scss">
.working-hrs-dialog {
  .stepper-page {
    background-color: var(--v-surface-base);
    padding: 32px;
    min-height: 400px;
  }

  .auto-start {
    border-radius: 8px;
    border: 1px solid #fff3;
    padding: 12px;
    background-color: #0002;
    font-size: 14px;
  }

  .form-field {
    max-width: 300px;
    margin: 12px 0;
  }

  .two-column-form {
    display: grid;
    grid-template-columns: calc(50% - 10px) calc(50% - 10px);
    grid-column-gap: 20px;
  }

  .form-field.large {
    max-width: 424px;
  }

  .form-group {
    border: 1px solid #fff3;
    padding: 12px 20px 0;
    border-radius: 4px;
  }

  .form-group + .form-group {
    margin-top: 20px;
  }

  .time-zone {
    display: grid;
    grid-template-columns: 160px auto;
    grid-column-gap: 20px;
  }

  .day-row {
    display: grid;
    grid-template-columns: 160px 160px 160px min-content;
    grid-column-gap: 20px;
    padding: 12px 32px 0;
    transition: all 0.2s ease;
    margin: 0 -32px;

    &:hover {
      background-color: #0001;

      .actions {
        opacity: 1;
      }
    }

    .actions {
      padding: 12px 0;
      white-space: nowrap;
      opacity: 0.5;
    }

    .day-label {
      font-size: 17px;
      color: var(--v-primary-base);
      padding: 12px 0;

      .detail {
        color: #fff;
        opacity: 0.6;
        font-size: 12px;
        margin-bottom: 12px;
      }
    }

    .from-to {
      display: grid;
      grid-template-columns: auto auto;
    }
  }

  .custom-holidays {
    header {
      display: grid;
      grid-template-columns: auto min-content;
    }
  }
  .note {
    font-size: 12px;
    opacity: 0.6;
    padding-bottom: 12px;
  }
}
</style>
<style>
.v-stepper__content {
  padding: 0 !important;
}
</style>
